import React, { useState, useEffect } from 'react';
import apiService from '../services/api';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import Layout from '../components/Layout';
import './SuperAdmin.scss';

const MySwal = withReactContent(Swal);

const SuperAdminDashboard = () => {
    const navigate = useNavigate();
    const [stats, setStats] = useState({ shops: 0, users: 0, active_shops: 0 });
    const [shops, setShops] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('shops');
    const [migrationRunning, setMigrationRunning] = useState(false);
    const [migrationOutput, setMigrationOutput] = useState(null);
    const [artisanMigrateRunning, setArtisanMigrateRunning] = useState(false);

    // Password Reset Override State
    const [showResetModal, setShowResetModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [resetLoading, setResetLoading] = useState(false);

    // Shop Form
    const [showShopModal, setShowShopModal] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [editingShopId, setEditingShopId] = useState(null);
    const [shopForm, setShopForm] = useState({
        name: '', admin_name: '', email: '', password: '',
        max_products: 100, max_employees: 5, max_customers: 1000
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const statsRes = await apiService.get('/super-admin/stats');
            setStats(statsRes.data);

            const shopsRes = await apiService.get('/super-admin/shops');
            setShops(shopsRes.data);

            const usersRes = await apiService.get('/super-admin/users');
            setUsers(usersRes.data);

            setLoading(false);
        } catch (error) {
            console.error("Error fetching super admin data", error);
            if (error.response && (error.response.status === 403 || error.response.status === 401)) {
                MySwal.fire({
                    icon: 'error',
                    title: 'Access Denied',
                    text: 'You do not have permission to access the Super Admin panel.',
                    confirmButtonColor: '#6C5DD3'
                });
                navigate('/');
            }
            setLoading(false);
        }
    };

    const handleSubmitShop = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            if (editingShopId) {
                await apiService.put(`/super-admin/shops/${editingShopId}`, shopForm);
            } else {
                await apiService.post('/super-admin/shops', shopForm);
            }

            setShowShopModal(false);
            setEditingShopId(null);
            setShopForm({
                name: '', admin_name: '', email: '', password: '',
                max_products: 100, max_employees: 5, max_customers: 1000
            });
            fetchData();

            MySwal.fire({
                icon: 'success',
                title: editingShopId ? 'Updated!' : 'Created!',
                text: editingShopId ? 'Shop updated successfully.' : 'New shop created successfully.',
                timer: 2000,
                showConfirmButton: false,
                toast: true,
                position: 'top-end'
            });
        } catch (error) {
            MySwal.fire('Error', error.response?.data?.message || 'Failed to save shop.', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const toggleShopStatus = async (id) => {
        const result = await MySwal.fire({
            title: 'Change Status?',
            text: "Are you sure you want to toggle this business account's status?",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, Toggle',
            confirmButtonColor: '#6C5DD3',
            cancelButtonColor: '#8F9BBA',
            reverseButtons: true
        });

        if (result.isConfirmed) {
            try {
                await apiService.post(`/super-admin/shops/${id}/toggle`, {});
                fetchData();
                MySwal.fire({
                    icon: 'success',
                    title: 'Updated',
                    text: 'Shop status changed successfully.',
                    timer: 1500,
                    showConfirmButton: false,
                    toast: true,
                    position: 'top-end'
                });
            } catch (error) {
                MySwal.fire('Error', 'Failed to update shop status.', 'error');
            }
        }
    };

    const toggleUserBlock = async (id) => {
        const result = await MySwal.fire({
            title: 'Update Access?',
            text: "Toggle block status for this user?",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, Proceed',
            confirmButtonColor: '#6C5DD3',
            cancelButtonColor: '#8F9BBA',
            reverseButtons: true
        });

        if (result.isConfirmed) {
            try {
                await apiService.post(`/super-admin/users/${id}/toggle-block`, {});
                fetchData();
                MySwal.fire({
                    icon: 'success',
                    title: 'Updated',
                    text: 'User access status updated.',
                    timer: 1500,
                    showConfirmButton: false,
                    toast: true,
                    position: 'top-end'
                });
            } catch (error) {
                MySwal.fire('Error', 'Failed to update user status.', 'error');
            }
        }
    };

    const handleLoginAs = async (userId) => {
        const result = await MySwal.fire({
            title: 'Account Impersonation',
            text: "You will be logged in as this administrator. You can return by re-logging.",
            icon: 'info',
            showCancelButton: true,
            confirmButtonText: 'Login As Admin',
            confirmButtonColor: '#3F8CFF',
            reverseButtons: true
        });

        if (result.isConfirmed) {
            try {
                const response = await apiService.post(`/super-admin/users/${userId}/login-as`, {});
                localStorage.setItem('token', response.data.access_token);
                localStorage.setItem('user', JSON.stringify(response.data.user));
                window.location.href = '/';
            } catch (error) {
                MySwal.fire('Error', 'Could not login as user.', 'error');
            }
        }
    };

    const handleRunMigrations = async () => {
        const result = await MySwal.fire({
            title: 'Database Sync',
            text: "Run all pending migrations? This is safe for production data.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Run Now',
            confirmButtonColor: '#FFB800',
            reverseButtons: true
        });

        if (result.isConfirmed) {
            setMigrationRunning(true);
            try {
                const response = await apiService.post('/super-admin/run-migrations', {});
                setMigrationOutput(response.data.output);
                MySwal.fire('Success', 'Database is now up to date.', 'success');
            } catch (error) {
                MySwal.fire('Error', 'Database synchronization failed.', 'error');
            } finally {
                setMigrationRunning(false);
            }
        }
    };

    const handleRunArtisanMigrate = async () => {
        const result = await MySwal.fire({
            title: 'Run Server Migration',
            text: "Execute 'php artisan migrate' command on the server? This will safely update database tables with any pending schemas. Existing records and data will NOT be disturbed.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Run Migration',
            confirmButtonColor: '#00d09c',
            cancelButtonColor: '#8F9BBA',
            reverseButtons: true
        });

        if (result.isConfirmed) {
            setArtisanMigrateRunning(true);
            try {
                const response = await apiService.post('/super-admin/run-db-migrate', {});
                setMigrationOutput(response.data.output);
                MySwal.fire({
                    icon: 'success',
                    title: 'Completed',
                    text: response.data.message || 'Artisan migrate command completed successfully.',
                    confirmButtonColor: '#00d09c'
                });
            } catch (error) {
                MySwal.fire({
                    icon: 'error',
                    title: 'Execution Failed',
                    text: error.response?.data?.message || 'Artisan migrate command failed to execute.',
                    confirmButtonColor: '#6C5DD3'
                });
            } finally {
                setArtisanMigrateRunning(false);
            }
        }
    };

    const handleOpenResetModal = (user) => {
        setSelectedUser(user);
        setNewPassword('');
        setConfirmPassword('');
        setShowResetModal(true);
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        if (newPassword.length < 8) {
            MySwal.fire('Invalid Password', 'Password must be at least 8 characters long.', 'error');
            return;
        }
        if (newPassword !== confirmPassword) {
            MySwal.fire('Password Mismatch', 'Passwords do not match.', 'error');
            return;
        }
        setResetLoading(true);
        try {
            await apiService.post(`/super-admin/users/${selectedUser.id}/reset-password`, {
                password: newPassword,
                password_confirmation: confirmPassword
            });
            MySwal.fire({
                icon: 'success',
                title: 'Password Updated!',
                text: `Password for administrator ${selectedUser.name} has been reset successfully.`,
                timer: 2500,
                showConfirmButton: false,
                toast: true,
                position: 'top-end'
            });
            setShowResetModal(false);
            setSelectedUser(null);
        } catch (error) {
            MySwal.fire('Reset Failed', error.response?.data?.message || 'Failed to reset administrator password.', 'error');
        } finally {
            setResetLoading(false);
        }
    };

    if (loading) return (
        <Layout title="Initializing Infrastructure...">
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                <div className="spinner-border text-primary" style={{width: '3rem', height: '3rem'}} role="status"></div>
            </div>
        </Layout>
    );

    return (
        <Layout title="Global Infrastructure Command">
            <div className="super-admin-workspace animate__animated animate__fadeIn">
                
                {/* Global Stats Overview */}
                <div className="row g-4 mb-4">
                    <div className="col-12 col-md-4">
                        <div className="sa-stats-card p-4 rounded-4 shadow-sm border-0">
                            <div className="d-flex align-items-center gap-3">
                                <div className="sa-icon-box bg-primary bg-opacity-10 text-primary"><i className="bi bi-buildings-fill fs-4"></i></div>
                                <div>
                                    <div className="text-muted small fw-bold text-uppercase ls-1">Total Entities</div>
                                    <h3 className="fw-extrabold mb-0">{stats.shops}</h3>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-12 col-md-4">
                        <div className="sa-stats-card p-4 rounded-4 shadow-sm border-0 text-white" style={{background: 'linear-gradient(135deg, #00d09c 0%, #00bfa5 100%)'}}>
                            <div className="d-flex align-items-center gap-3">
                                <div className="sa-icon-box bg-white bg-opacity-20"><i className="bi bi-check-circle-fill fs-4 text-warning"></i></div>
                                <div>
                                    <div className="text-white text-opacity-75 small fw-bold text-uppercase ls-1">Active Business Units</div>
                                    <h3 className="fw-extrabold mb-0">{stats.active_shops}</h3>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-12 col-md-4">
                        <div className="sa-stats-card p-4 rounded-4 shadow-sm border-0">
                            <div className="d-flex align-items-center gap-3">
                                <div className="sa-icon-box bg-info bg-opacity-10 text-info"><i className="bi bi-shield-lock-fill fs-4"></i></div>
                                <div>
                                    <div className="text-muted small fw-bold text-uppercase ls-1">System Administrators</div>
                                    <h3 className="fw-extrabold mb-0">{stats.users}</h3>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Management Tabs */}
                <div className="sa-tabs-modern-wrapper mb-4 overflow-x-auto pb-2">
                    <div className="sa-tabs-modern p-2 rounded-4 shadow-sm d-flex gap-2 w-max-content">
                        <button className={`sa-tab-btn flex-shrink-0 ${activeTab === 'shops' ? 'active' : ''}`} onClick={() => setActiveTab('shops')}>
                            <i className="bi bi-shop me-2"></i> Business Ecosystem
                        </button>
                        <button className={`sa-tab-btn flex-shrink-0 ${activeTab === 'users' ? 'active' : ''}`} onClick={() => setActiveTab('users')}>
                            <i className="bi bi-people-fill me-2"></i> Administrator Accounts
                        </button>
                        <button className={`sa-tab-btn flex-shrink-0 ${activeTab === 'system' ? 'active' : ''}`} onClick={() => setActiveTab('system')}>
                            <i className="bi bi-terminal-fill me-2"></i> Infrastructure Tools
                        </button>
                    </div>
                </div>

                {/* Primary Workspace Card */}
                <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
                    <div className="card-header bg-transparent p-4 border-0">
                        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
                            <div>
                                <h5 className="fw-bold mb-0">
                                    {activeTab === 'shops' ? 'Entity Directory' : activeTab === 'users' ? 'System User Registry' : 'System Command Terminal'}
                                </h5>
                                <small className="text-muted">High-privilege controls for global system management</small>
                            </div>
                            {activeTab === 'shops' && (
                                <button className="btn btn-primary rounded-pill px-4 fw-bold shadow-sm d-flex align-items-center gap-2" onClick={() => { setEditingShopId(null); setShowShopModal(true); }}>
                                    <i className="bi bi-plus-circle-fill"></i> Add Business Shop
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="card-body p-0">
                        {activeTab === 'shops' && (
                            <div className="table-responsive">
                                <table className="table table-hover align-middle mb-0 sa-modern-table">
                                    <thead>
                                        <tr>
                                            <th className="ps-4">Entity Identity</th>
                                            <th>Administrative Lead</th>
                                            <th>Resource Allocation</th>
                                            <th>Operational Status</th>
                                            <th className="text-end pe-4">Global Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {shops.length > 0 ? shops.map(shop => (
                                            <tr key={shop.id}>
                                                <td className="ps-4">
                                                    <div className="d-flex align-items-center gap-3">
                                                        <div className="sa-avatar-box bg-primary bg-opacity-10 text-primary fw-bold">
                                                            {shop.name.charAt(0).toUpperCase()}
                                                        </div>
                                                        <div>
                                                            <div className="fw-bold mb-0">{shop.name}</div>
                                                            <span className="text-muted x-small ls-1 text-uppercase">{shop.slug}</span>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    {shop.users?.[0] ? (
                                                        <div className="d-flex align-items-center gap-2">
                                                            <div className="avatar-sm bg-light border rounded-circle d-flex align-items-center justify-content-center" style={{ width: 32, height: 32, fontSize: '0.75rem' }}>
                                                                {shop.users[0].name.charAt(0)}
                                                            </div>
                                                            <div>
                                                                <div className="small fw-bold">{shop.users[0].name}</div>
                                                                <div className="text-muted x-small">{shop.users[0].email}</div>
                                                            </div>
                                                        </div>
                                                    ) : <span className="badge bg-danger bg-opacity-10 text-danger x-small">Unassigned</span>}
                                                </td>
                                                <td>
                                                    <div className="d-flex gap-3">
                                                        <div className="text-center" title="Products">
                                                            <div className="fw-bold small">{shop.max_products}</div>
                                                            <div className="x-small text-muted text-uppercase">Items</div>
                                                        </div>
                                                        <div className="v-divider" style={{height:'25px'}}></div>
                                                        <div className="text-center" title="Employees">
                                                            <div className="fw-bold small">{shop.max_employees}</div>
                                                            <div className="x-small text-muted text-uppercase">Staff</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <span className={`badge rounded-pill px-3 py-1 x-small fw-bold ${shop.status === 'active' ? 'bg-success bg-opacity-10 text-success' : 'bg-danger bg-opacity-10 text-danger'}`}>
                                                        <i className={`bi bi-${shop.status === 'active' ? 'check-circle' : 'x-circle'} me-1`}></i>
                                                        {shop.status.toUpperCase()}
                                                    </span>
                                                </td>
                                                <td className="text-end pe-4">
                                                    <div className="d-flex justify-content-end gap-2">
                                                        <button className="btn btn-icon-modern bg-primary bg-opacity-10 text-primary border-0" title="Edit Entity" onClick={() => {
                                                            setEditingShopId(shop.id);
                                                            setShopForm({
                                                                name: shop.name,
                                                                max_products: shop.max_products,
                                                                max_employees: shop.max_employees,
                                                                max_customers: shop.max_customers
                                                            });
                                                            setShowShopModal(true);
                                                        }}><i className="bi bi-pencil-square"></i></button>
                                                        <button className={`btn-icon-modern ${shop.status === 'active' ? 'bg-danger bg-opacity-10 text-danger' : 'bg-success bg-opacity-10 text-success'} border-0`} 
                                                                title={shop.status === 'active' ? 'Block Access' : 'Restore Access'} 
                                                                onClick={() => toggleShopStatus(shop.id)}>
                                                            <i className={`bi bi-${shop.status === 'active' ? 'slash-circle-fill' : 'play-circle-fill'}`}></i>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        )) : (
                                            <tr><td colSpan="5" className="text-center py-5 text-muted">No business entities detected.</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {activeTab === 'users' && (
                            <div className="table-responsive">
                                <table className="table table-hover align-middle mb-0 sa-modern-table">
                                    <thead>
                                        <tr>
                                            <th className="ps-4">Identity Profile</th>
                                            <th>Organizational Link</th>
                                            <th>Access Authority</th>
                                            <th className="text-end pe-4">Identity Controls</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {users.length > 0 ? users.map(user => (
                                            <tr key={user.id}>
                                                <td className="ps-4">
                                                    <div className="d-flex align-items-center gap-3">
                                                        <div className="sa-avatar-box bg-info bg-opacity-10 text-info fw-bold">
                                                            {user.name.charAt(0).toUpperCase()}
                                                        </div>
                                                        <div>
                                                            <div className="fw-bold mb-0">{user.name}</div>
                                                            <small className="text-muted">{user.email}</small>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <span className="badge bg-primary bg-opacity-10 text-primary rounded-pill px-3 py-1 x-small fw-bold">
                                                        <i className="bi bi-shop me-1"></i>
                                                        {user.shop?.name || 'Independent Admin'}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className={`badge rounded-pill px-3 py-1 x-small fw-bold ${!user.is_blocked ? 'bg-success bg-opacity-10 text-success' : 'bg-danger bg-opacity-10 text-danger'}`}>
                                                        <i className={`bi bi-${!user.is_blocked ? 'shield-check' : 'shield-exclamation'} me-1`}></i>
                                                        {!user.is_blocked ? 'ACTIVE' : 'RESTRICTED'}
                                                    </span>
                                                </td>
                                                <td className="text-end pe-4">
                                                    <div className="d-flex justify-content-end gap-2">
                                                        <button className="btn btn-outline-primary btn-sm rounded-pill px-3 fw-bold" onClick={() => handleLoginAs(user.id)}>
                                                            <i className="bi bi-incognito me-1"></i> Impersonate
                                                        </button>
                                                        <button className="btn btn-outline-warning btn-sm rounded-pill px-3 fw-bold animate__animated animate__fadeIn" onClick={() => handleOpenResetModal(user)}>
                                                            <i className="bi bi-key me-1"></i> Reset Password
                                                        </button>
                                                        <button className={`btn-icon-modern ${!user.is_blocked ? 'bg-danger bg-opacity-10 text-danger' : 'bg-success bg-opacity-10 text-success'} border-0`} 
                                                                title={!user.is_blocked ? 'Revoke Access' : 'Grant Access'} 
                                                                onClick={() => toggleUserBlock(user.id)}>
                                                            <i className={`bi bi-${!user.is_blocked ? 'person-fill-dash' : 'person-fill-check'}`}></i>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        )) : (
                                            <tr><td colSpan="4" className="text-center py-5 text-muted">No system administrators registered.</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {activeTab === 'system' && (
                            <div className="sa-system-tab-content p-3 p-md-5">
                                <div className="row g-4 justify-content-center">
                                    <div className="col-12 col-lg-7">
                                        <div className="system-command-box text-center p-4 p-md-5 rounded-4 border-0 shadow-sm" style={{background: 'var(--bg-color)'}}>
                                            <div className="sa-icon-box bg-warning bg-opacity-10 text-warning mx-auto mb-4" style={{width:'70px', height:'70px'}}>
                                                <i className="bi bi-database-fill-gear fs-2"></i>
                                            </div>
                                            <h4 className="fw-bold">Infrastructure Synchronization</h4>
                                            <p className="text-muted small mb-4">Align the global database schema with current build specifications. Safe for production tenant data.</p>
                                            
                                            <div className="alert bg-info bg-opacity-10 text-info border-0 rounded-4 mb-4 text-start shadow-sm d-flex align-items-start gap-3">
                                                <i className="bi bi-shield-lock-fill fs-5 mt-1"></i>
                                                <div className="x-small">
                                                    <strong className="d-block mb-1">Security Protocol:</strong> 
                                                    Uses standard Laravel Artisan protocols. Non-destructive sync.
                                                </div>
                                            </div>

                                            <div className="d-flex flex-column flex-md-row justify-content-center gap-3">
                                                <button className="btn btn-warning btn-lg rounded-pill px-4 fw-bold shadow-sm w-100 w-md-auto" 
                                                        onClick={handleRunMigrations} 
                                                        disabled={migrationRunning || artisanMigrateRunning}>
                                                    {migrationRunning ? (
                                                        <><span className="spinner-border spinner-border-sm me-2"></span> SYNCING...</>
                                                    ) : <><i className="bi bi-play-fill me-2"></i>INITIALIZE SYNC</>}
                                                </button>

                                                <button className="btn btn-success btn-lg text-white rounded-pill px-4 fw-bold shadow-sm w-100 w-md-auto" 
                                                        onClick={handleRunArtisanMigrate} 
                                                        disabled={migrationRunning || artisanMigrateRunning}>
                                                    {artisanMigrateRunning ? (
                                                        <><span className="spinner-border spinner-border-sm me-2"></span> RUNNING...</>
                                                    ) : <><i className="bi bi-database-fill-gear me-2"></i>RUN ARTISAN MIGRATE</>}
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-12 col-lg-5">
                                        <div className="card h-100 border-0 shadow-sm rounded-4 p-4" style={{background: 'var(--bg-color)'}}>
                                            <h6 className="fw-bold mb-3 text-uppercase ls-1 small"><i className="bi bi-activity me-2"></i>System Health</h6>
                                            <div className="d-flex flex-column gap-3">
                                                <div className="d-flex justify-content-between align-items-center p-3 rounded-3 bg-light bg-opacity-50">
                                                    <span className="small text-muted">Database Engine</span>
                                                    <span className="badge bg-success bg-opacity-10 text-success rounded-pill px-3">STABLE</span>
                                                </div>
                                                <div className="d-flex justify-content-between align-items-center p-3 rounded-3 bg-light bg-opacity-50">
                                                    <span className="small text-muted">Storage Cluster</span>
                                                    <span className="badge bg-success bg-opacity-10 text-success rounded-pill px-3">OPTIMAL</span>
                                                </div>
                                                <div className="d-flex justify-content-between align-items-center p-3 rounded-3 bg-light bg-opacity-50">
                                                    <span className="small text-muted">API Latency</span>
                                                    <span className="badge bg-info bg-opacity-10 text-info rounded-pill px-3">24ms</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                {migrationOutput && (
                                    <div className="mt-4 w-100 animate__animated animate__fadeIn">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <h6 className="text-uppercase fw-bold text-muted small mb-0"><i className="bi bi-terminal me-2"></i>System Sync Log Output</h6>
                                            <span className="badge bg-success bg-opacity-10 text-success rounded-pill x-small px-3">COMPLETED</span>
                                        </div>
                                        <div className="bg-dark text-white p-3 p-md-4 rounded-4 shadow-lg border border-secondary border-opacity-25" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                            <pre className="m-0" style={{ color: '#00FF41', fontFamily: 'monospace', fontSize: '0.8rem' }}>{migrationOutput}</pre>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Modal Overhaul */}
            {showShopModal && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(27,37,89,0.7)', backdropFilter: 'blur(12px)' }} onClick={() => setShowShopModal(false)}>
                    <div className="modal-dialog modal-dialog-centered" onClick={e => e.stopPropagation()}>
                        <form onSubmit={handleSubmitShop} className="modal-content border-0 shadow-lg rounded-4 overflow-hidden">
                            <div className="modal-header border-0 p-4 pb-0">
                                <div className="d-flex align-items-center gap-3">
                                    <div className="sa-icon-box bg-primary bg-opacity-10 text-primary"><i className="bi bi-plus-circle-fill fs-4"></i></div>
                                    <div>
                                        <h5 className="modal-title fw-bold">{editingShopId ? 'Update Entity Parameters' : 'Register New Business Unit'}</h5>
                                        <small className="text-muted">Configure core quotas and identity for the new shop</small>
                                    </div>
                                </div>
                                <button type="button" className="btn-close shadow-none" onClick={() => setShowShopModal(false)}></button>
                            </div>
                            <div className="modal-body p-4">
                                <div className="mb-4">
                                    <label className="form-label text-muted small fw-bold text-uppercase ls-1">Company / Shop Identity</label>
                                    <input type="text" className="form-control sa-input" placeholder="e.g. Apex Global Mart" value={shopForm.name} onChange={e => setShopForm({...shopForm, name: e.target.value})} required />
                                </div>
                                {!editingShopId && (
                                    <div className="p-4 rounded-4 mb-4 bg-light bg-opacity-50 border border-dashed">
                                        <h6 className="fw-bold mb-3 small text-uppercase text-primary ls-1">Owner Credentials</h6>
                                        <div className="mb-3">
                                            <input type="text" className="form-control sa-input mb-2" placeholder="Full Administrative Name" value={shopForm.admin_name} onChange={e => setShopForm({...shopForm, admin_name: e.target.value})} required />
                                        </div>
                                        <div className="row g-2">
                                            <div className="col-12 col-md-6">
                                                <input type="email" className="form-control sa-input" placeholder="Primary Email Address" value={shopForm.email} onChange={e => setShopForm({...shopForm, email: e.target.value})} required />
                                            </div>
                                            <div className="col-12 col-md-6">
                                                <input type="password" className="form-control sa-input" placeholder="Temporary Password" value={shopForm.password} onChange={e => setShopForm({...shopForm, password: e.target.value})} required />
                                            </div>
                                        </div>
                                    </div>
                                )}
                                <div className="row g-3">
                                    <div className="col-4">
                                        <label className="form-label text-muted small fw-bold text-uppercase ls-1">Inventory</label>
                                        <input type="number" className="form-control sa-input" value={shopForm.max_products} onChange={e => setShopForm({...shopForm, max_products: e.target.value})} />
                                    </div>
                                    <div className="col-4">
                                        <label className="form-label text-muted small fw-bold text-uppercase ls-1">Workforce</label>
                                        <input type="number" className="form-control sa-input" value={shopForm.max_employees} onChange={e => setShopForm({...shopForm, max_employees: e.target.value})} />
                                    </div>
                                    <div className="col-4">
                                        <label className="form-label text-muted small fw-bold text-uppercase ls-1">Clients</label>
                                        <input type="number" className="form-control sa-input" value={shopForm.max_customers} onChange={e => setShopForm({...shopForm, max_customers: e.target.value})} />
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer border-0 p-4 pt-0">
                                <button type="button" className="btn btn-light rounded-pill px-4 fw-bold" onClick={() => setShowShopModal(false)}>Discard</button>
                                <button type="submit" className="btn btn-primary rounded-pill px-5 fw-bold shadow-sm" disabled={isSubmitting}>
                                    {isSubmitting ? <><span className="spinner-border spinner-border-sm me-2"></span>Committing...</> : <><i className={`bi bi-${editingShopId ? 'check2-circle' : 'cloud-arrow-up-fill'} me-2`}></i>{editingShopId ? 'Commit Changes' : 'Initialize Shop'}</>}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Super Admin Password Reset Modal */}
            {showResetModal && selectedUser && (
                <div className="modal show d-block animate__animated animate__fadeIn" style={{ backgroundColor: 'rgba(27,37,89,0.7)', backdropFilter: 'blur(12px)' }} onClick={() => setShowResetModal(false)}>
                    <div className="modal-dialog modal-dialog-centered" onClick={e => e.stopPropagation()}>
                        <form onSubmit={handleResetPassword} className="modal-content border-0 shadow-lg rounded-4 overflow-hidden">
                            <div className="modal-header border-0 p-4 pb-0">
                                <div className="d-flex align-items-center gap-3">
                                    <div className="sa-icon-box bg-warning bg-opacity-10 text-warning"><i className="bi bi-key-fill fs-4"></i></div>
                                    <div>
                                        <h5 className="modal-title fw-bold">Reset Administrator Password</h5>
                                        <small className="text-muted">Override credentials for tenant admin <strong>{selectedUser.name}</strong></small>
                                    </div>
                                </div>
                                <button type="button" className="btn-close shadow-none" onClick={() => setShowResetModal(false)}></button>
                            </div>
                            <div className="modal-body p-4">
                                <div className="mb-3 text-start">
                                    <label className="form-label text-muted small fw-bold text-uppercase ls-1">New Password</label>
                                    <input
                                        type="password"
                                        className="form-control sa-input"
                                        placeholder="Min 8 characters"
                                        value={newPassword}
                                        onChange={e => setNewPassword(e.target.value)}
                                        required
                                    />
                                </div>
                                <div className="mb-4 text-start">
                                    <label className="form-label text-muted small fw-bold text-uppercase ls-1">Confirm New Password</label>
                                    <input
                                        type="password"
                                        className="form-control sa-input"
                                        placeholder="Repeat new password"
                                        value={confirmPassword}
                                        onChange={e => setConfirmPassword(e.target.value)}
                                        required
                                    />
                                </div>
                            </div>
                            <div className="modal-footer border-0 p-4 pt-0">
                                <button type="button" className="btn btn-light rounded-pill px-4 fw-bold" onClick={() => setShowResetModal(false)}>Cancel</button>
                                <button type="submit" className="btn btn-warning rounded-pill px-5 fw-bold text-dark shadow-sm" disabled={resetLoading}>
                                    {resetLoading ? <><span className="spinner-border spinner-border-sm me-2"></span>Resetting...</> : 'Confirm Override'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <style>{`
                .sa-stats-card { background: var(--card-bg); border: 1px solid var(--border-color) !important; transition: all 0.3s ease; }
                .sa-stats-card:hover { transform: translateY(-5px); box-shadow: 0 10px 25px rgba(0,0,0,0.1) !important; }
                .sa-icon-box { width: 52px; height: 52px; border-radius: 14px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
                
                .sa-tabs-modern { background: var(--bg-color); border: 1px solid var(--border-color); width: fit-content; }
                .sa-tabs-modern-wrapper::-webkit-scrollbar { height: 4px; }
                .sa-tabs-modern-wrapper::-webkit-scrollbar-thumb { background: var(--border-color); border-radius: 10px; }
                .sa-tab-btn { border: none; background: transparent; padding: 10px 20px; border-radius: 12px; font-weight: 700; font-size: 0.85rem; color: var(--text-muted-color); transition: all 0.2s ease; white-space: nowrap; }
                .sa-tab-btn.active { background: var(--primary-color); color: #fff; box-shadow: 0 5px 15px rgba(108, 93, 211, 0.2); }
                
                .sa-modern-table thead th { background: var(--bg-color); color: var(--text-muted-color); font-size: 0.7rem; text-transform: uppercase; letter-spacing: 1.5px; font-weight: 800; padding: 18px 12px; border: none; }
                .sa-modern-table tbody td { padding: 18px 12px; border-bottom: 1px solid var(--border-color); }
                .sa-avatar-box { width: 42px; height: 42px; border-radius: 12px; display: flex; align-items: center; justify-content: center; }
                
                .sa-input { background: var(--bg-color); border: 1.5px solid var(--border-color); border-radius: 14px; padding: 12px 16px; font-weight: 500; transition: all 0.3s ease; }
                .sa-input:focus { background: var(--card-bg); border-color: var(--primary-color); box-shadow: none; }
                
                .btn-icon-modern { width: 36px; height: 36px; border-radius: 10px; display: flex; align-items: center; justify-content: center; transition: all 0.2s ease; }
                .btn-icon-modern:hover { transform: scale(1.1); }

                @media (max-width: 768px) {
                    .sa-tabs-modern { width: max-content; }
                    .card-header .d-flex { flex-direction: column; align-items: flex-start !important; }
                    .card-header .btn { width: 100%; margin-top: 10px; }
                }

                [data-bs-theme="dark"] .sa-stats-card { background: #1A1A2E; }
                [data-bs-theme="dark"] .card { background: #1A1A2E; }
                [data-bs-theme="dark"] .modal-content { background: #1A1A2E; border: 1px solid rgba(255,255,255,0.05); }
                [data-bs-theme="dark"] .sa-modern-table thead th { background: rgba(255,255,255,0.02); }
                [data-bs-theme="dark"] .sa-tabs-modern { background: rgba(255,255,255,0.02); }
            `}</style>
        </Layout>
    );
};

export default SuperAdminDashboard;
